import { Button, HStack, Select } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';

import { Util } from '@finley-technologies/finley-types';

import { useGetPipelineRunInputsQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';

type EarlierFile = { fileVersion: number; uploadedAt: number };

/**
 * The Upload modal's secondary option: fill the input with the file an earlier
 * version of the date received, instead of uploading one. A low-key link reveals the
 * choice of version; with no earlier file to offer, nothing renders.
 */
export const EarlierVersionFile = ({
  implementationId,
  reportingDate,
  fileVersion,
  artifactSlug,
  onKeep,
  onClose,
}: {
  implementationId: string;
  reportingDate: Util.FullDateStr;
  // the version being filled in; its own file is not offered
  fileVersion: number;
  artifactSlug: string;
  onKeep: (fromFileVersion: number) => void;
  onClose: () => void;
}): React.ReactElement | null => {
  const { data } = useGetPipelineRunInputsQuery({
    variables: {
      implementationId,
      reportingDate,
      artifactSlugs: [artifactSlug],
    },
    fetchPolicy: 'network-only',
  });

  // newest first, as the runs are listed; only files received successfully
  const earlierFiles: EarlierFile[] = useMemo(
    () =>
      (data?.getPipelineRunInputs ?? []).flatMap((run): EarlierFile[] =>
        run.fileVersion === fileVersion
          ? []
          : run.inputs
              .filter(
                (input) =>
                  input.artifactSlug === artifactSlug &&
                  input.status === 'succeeded',
              )
              .map(({ uploadedAt }) => ({
                fileVersion: run.fileVersion,
                uploadedAt,
              })),
      ),
    [artifactSlug, data, fileVersion],
  );

  const [isRevealed, setIsRevealed] = useState<boolean>(false);
  const [selected, setSelected] = useState<number | null>(null);

  if (earlierFiles.length === 0) {
    return null;
  }
  if (!isRevealed) {
    return (
      <Button
        variant="link"
        size="sm"
        fontWeight="normal"
        alignSelf="flex-start"
        onClick={() => setIsRevealed(true)}
      >
        Or use the file from an earlier version
      </Button>
    );
  }

  const chosen = selected ?? earlierFiles[0].fileVersion;
  return (
    <HStack alignSelf="flex-start" spacing="10px">
      <Select
        size="sm"
        width="auto"
        aria-label="Earlier version"
        value={chosen}
        onChange={(event) => setSelected(Number(event.target.value))}
      >
        {earlierFiles.map((earlierFile) => (
          <option key={earlierFile.fileVersion} value={earlierFile.fileVersion}>
            {`v${earlierFile.fileVersion} · uploaded ${DateTime.fromMillis(
              earlierFile.uploadedAt,
            ).toFormat('MMM d')}`}
          </option>
        ))}
      </Select>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          onKeep(chosen);
          onClose();
        }}
      >
        Use this file
      </Button>
    </HStack>
  );
};
